import React, { useEffect, useReducer, useContext, useState } from "react";
import { enumerate } from "common/utils";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import ListItems from "./ListItems";
import ModificationsModal from "./Modifications";
import ImportModal, { importActionTriggers } from "./Import";

const doesNothing = () => {};

const higherId = (current, { __id }) => (current > __id ? current : __id);
const nextId = (items) => items.reduce(higherId, 0) + 1;

function init({ defaults = {}, from = "unknown" }) {
  const source = defaults[from] || [];
  return {
    changed: false,
    list: enumerate(0, { stored: defaults.stored })([...source]),
  };
}
const deleteItemsIn = (list, id = undefined) => [
  ...list.flatMap(({ __id, stored, ...item }) =>
    id !== undefined && __id !== id
      ? [{ __id, stored, ...item }]
      : stored === true
      ? [{ __id, stored, ...item, deleted: true }]
      : []
  ),
];

function contentsReducer({ list }, { type, ...action }) {
  switch (type) {
    case "added": {
      return {
        changed: true,
        list: [
          ...list,
          {
            ...action.item,
            stored: false,
            __id: nextId(list),
          },
        ],
      };
    }
    case "deleted": {
      return {
        changed: true,
        list: deleteItemsIn(list, action.id),
      };
    }
    case "updated": {
      let found = false;
      return {
        changed: true,
        list: [
          // Update the list
          ...list.map(({ __id, ...item }) =>
            __id !== action.update.__id
              ? { __id, ...item }
              : { __id, ...item, ...action.update, modified: (found = true) }
          ),
          ...(found === true
            ? [
                /*nothing to add*/
              ]
            : [action.update]),
        ],
      };
    }
    case "reset":
      return init(action.payload);
    case "batch": {
      const previous = action.method === "replace" ? deleteItemsIn(list) : list;
      return {
        changed: true,
        list: [
          ...previous,
          ...enumerate(nextId(previous), { stored: false })(action.update),
        ],
      };
    }
    default: {
      throw Error("Unknown action: " + type);
    }
  }
}

const ActionsLayout = styled.div`
  margin-bottom: 30px;
`;

const Actions = ({ are = [], sends = doesNothing }) => (
  <ActionsLayout>
    {are.map(({ label, signal, icon = "add_circle", method, ...params }) => (
      <button
        key={method ? `${signal}-${method}` : signal}
        type="button"
        onClick={() => sends(signal, { method, ...params })}
        className="btn btn-default waves-effect shadow-none"
      >
        <i className="material-icons">{icon}</i>
        <span>{label}...</span>
      </button>
    ))}
  </ActionsLayout>
);

const creationTrigger = ({ cliName, labelSingle, label }) => ({
  signal: `show-create-${cliName}-modal`,
  label: `Add ${labelSingle || label}`,
});

const HasMany = ({
  label = undefined,
  labelSingle = "",
  className = "",
  items = [],
  fields = [],
  cliName,
  onError = doesNothing,
  onDelete = doesNothing,
  onCreate = doesNothing,
  onUpdate = doesNothing,
  getItems = doesNothing,
  onBatch = doesNothing,
  importable = false,
  customActions = [],
  importLineParse = undefined,
}) => {
  const actions = useContext(ActionsContext);
  const itemById = (id) => items.find(({ __id }) => __id === id);
  const sendEdit = (id) =>
    actions.send(`show-edit-${cliName}-modal`, itemById(id));
  const sends = (signal, ...args) => actions.send(signal, ...args);

  return (
    <>
      <Actions
        are={[
          creationTrigger({ cliName, labelSingle, label }),
          ...importActionTriggers({ cliName, label, labelSingle, importable }),
          ...customActions,
        ]}
        sends={sends}
      />
      <ListItems
        items={items}
        fields={fields}
        className={className}
        onEdit={sendEdit}
        onDelete={onDelete}
      />
      <ModificationsModal
        cliName={cliName}
        labelSingle={labelSingle}
        fields={fields}
        onUpdate={onUpdate}
        onCreate={onCreate}
        getItems={getItems}
        modalError={onError}
      />
      {importable !== true ? null : (
        <ImportModal
          label={label}
          labelSingle={labelSingle}
          cliName={cliName}
          fields={fields}
          doBatch={onBatch}
          modalError={onError}
          importLineParse={importLineParse}
        />
      )}
      {customActions.map(({ signal, label, modal: CustomModal }) => (
        <CustomModal
          key={signal}
          signal={signal}
          title={label}
          onUpdate={onUpdate}
          onCreate={onCreate}
          getItems={getItems}
          modalError={onError}
          doBatch={onBatch}
          importLineParse={importLineParse}
        />
      ))}
    </>
  );
};

const ContentsLayout = styled.div``;

const Error = ({ children }) => (
  <div className="error align-left color-red">{children}</div>
);

const itsOk = (item) => item;

const tellAllowedAndFailedGiven =
  (validate = itsOk, others = []) =>
  ({ allowed, failed }, item, line) => {
    try {
      return { allowed: [...allowed, validate(item, others)], failed };
    } catch (error) {
      console.error(error);
      return { allowed, failed: [...failed, { line, error }] };
    }
  };

const ProfileContents = ({
  name: from = "many",
  fields = [],
  defaults = { stored: true },
  onChange = doesNothing,
  validate = itsOk,
  ...props
}) => {
  const payload = { defaults, from };
  const [contents, dispatch] = useReducer(contentsReducer, payload, init);
  const [error, setError] = useState(null);

  const handleDeleteId = (id) => {
    dispatch({
      type: "deleted",
      id,
    });
  };
  const createItem = (item) => {
    dispatch({
      type: "added",
      item: validate(item, contents.list),
    });
  };
  const updateItem = (update) => {
    dispatch({
      type: "updated",
      update: validate(update, contents.list),
    });
  };
  const getItems = () => [...contents.list];
  const reset = () => dispatch({ type: "reset", payload });

  const doBatch = (all, method = "update") =>
    Promise.resolve(
      all.reduce(
        tellAllowedAndFailedGiven(
          validate,
          method === "replace" ? [] : contents.list
        ),
        { allowed: [], failed: [] }
      )
    ).then(
      ({ allowed, failed }) => (
        dispatch({ type: "batch", method, update: allowed }),
        { allowed, failed }
      )
    );

  useEffect(() => {
    if (contents.changed === true) {
      onChange(null, { name: from, value: contents.list });
    }
  }, [contents]);
  useEffect(() => {
    if (contents.changed === true) {
      reset();
    }
  }, [from, JSON.stringify(defaults)]);
  return (
    <ContentsLayout className="full-width margin-t-10 margin-b-10">
      <HasMany
        items={contents.list}
        fields={fields}
        onDelete={handleDeleteId}
        onCreate={createItem}
        onUpdate={updateItem}
        onBatch={doBatch}
        getItems={getItems}
        kind={name}
        {...props}
        onError={setError}
      />
      {error === null ? null : <Error>{error}</Error>}
    </ContentsLayout>
  );
};

export default ProfileContents;
